import { Box, IconButton, styled, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import { StatusDot } from "../../../Components/StyledComponent";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CartLoader from "../../../Helper/Loaders/CartLoader";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

const SliderActionButton = styled(IconButton)(({ position, disabled }) => ({
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    left: position === "left" ? "-40px" : null,
    right: position === "right" ? "-40px" : null,
    top: "-5px",
    padding: "6px",
    background: "#F3F5F7 !important",
    "& .MuiSvgIcon-root": {
        color: disabled ? "#B2B2B2" : "#000000",
        fontSize: "10px",
        marginLeft: "auto",
        fontWeight: 500,
    },
    "& .MuiIconButton-root.Mui-disabled": {
        background: "#F3F5F7 !important",
    },
    "&:hover": {
        backgroundColor: "#D5D7FF",
        "& .MuiSvgIcon-root": {
            color: "#4D44D8",
        },
    },
}));

function CarouselSlider({ position, swiperRef }) {
    const handleClick = () => {
        if (!swiperRef.current) return;
        if (position === "left") {
            swiperRef.current.slidePrev();
        } else {
            swiperRef.current.slideNext();
        }
    };

    return (
        <SliderActionButton position={position} onClick={handleClick}>
            {position === "left" ? <ArrowBackIosNewIcon /> : <ArrowForwardIosIcon />}
        </SliderActionButton>
    );
}

const FleetChartLegend = ({ legendSeries, chartIDs, isLoading = false, centered = false }) => {
    const [visibleSeries, setVisibleSeries] = useState(
        legendSeries.map(() => true)
    );
    const [showArrows, setShowArrows] = useState(false);

    const swiperRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        setVisibleSeries(legendSeries.map(() => true));

        // check overflow manually
        if (containerRef.current) {
            const containerWidth = containerRef.current.offsetWidth;
            const totalWidth = Array.from(
                containerRef.current.querySelectorAll(".swiper-slide")
            ).reduce((acc, el) => acc + el.offsetWidth, 0);

            setShowArrows(totalWidth > containerWidth);
        }
    }, [legendSeries]);

    const toggleSeriesOnLegendClick = (index) => {
        const updatedVisibility = [...visibleSeries];
        updatedVisibility[index] = !updatedVisibility[index];
        setVisibleSeries(updatedVisibility);

        const seriesName = legendSeries[index].name;
        chartIDs.forEach((cID) => {
            if (updatedVisibility[index]) {
                ApexCharts.exec(cID, "showSeries", seriesName);
            } else {
                ApexCharts.exec(cID, "hideSeries", seriesName);
            }
        });
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                }}
            >
                <CartLoader />
            </Box>
        );
    }

    return (
        <Box
            className="comparison-legend-view"
            sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
            }}
        >
            {/* Left Arrow */}
            {showArrows && <CarouselSlider position="left" swiperRef={swiperRef} />}

            <Box
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    "& .swiper-wrapper": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: centered ? "center" : "flex-start",
                    },
                    "& .swiper-slide": {
                        width: "auto !important", // variable width
                    },
                }}
                ref={containerRef}
            >
                <Swiper
                    modules={[Navigation]}
                    slidesPerView="auto"
                    spaceBetween={8}
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                >
                    {legendSeries.map((s, i) => (
                        <SwiperSlide key={`${s.name}-${i}`}>
                            <Box
                                onClick={() => toggleSeriesOnLegendClick(i)}
                                sx={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    opacity: visibleSeries[i] ? 1 : 0.5,
                                }}
                            >
                                <StatusDot sx={{ marginRight: "3px" }} color={s.color} />
                                <Typography
                                    sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                    variant="fw400s12c3D4B5A"
                                >
                                    {s.name}
                                </Typography>
                            </Box>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>

            {/* Right Arrow */}
            {showArrows && <CarouselSlider position="right" swiperRef={swiperRef} />}
        </Box>
    );
};

export default FleetChartLegend;
